import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Lock, Mail } from "lucide-react";

/**
 * Guest → Registered upgrade flow. Preserves the current @handle + history —
 * no need to sign out, sign back in, or pick a new username. Adds email +
 * password to the existing account so voice, images, bookmarks, etc. unlock
 * across future sessions.
 */
export default function UpgradeGuestModal({ open, onClose }) {
  const { user, setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || password.length < 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/upgrade", { email: email.trim(), password });
      setSession(data.token, data.user);
      toast.success(`WELCOME, ${data.user.username.toUpperCase()} — YOU'RE LOCKED IN`);
      onClose?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-obsidian/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="upgrade-modal"
    >
      <div
        className="bg-obsidian border-2 border-acid w-full max-w-md p-6 shadow-[8px_8px_0_#00E5FF]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono text-acid text-[10px] tracking-widest">// UPGRADE ACCOUNT</div>
            <h3 className="font-heading text-3xl md:text-4xl mt-1 leading-none">
              KEEP <span className="text-acid">@{user?.username}</span>.<br />
              GO PRO.
            </h3>
            <p className="font-mono text-white/60 text-xs mt-3 leading-relaxed">
              &gt; Add an email + password to your guest account. Same handle,
              same history — unlocks voice notes, images, bookmarks + you can
              sign back in from any device.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-blaze shrink-0"
            data-testid="upgrade-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="upgrade-form">
          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/60 flex items-center gap-1">
              <Mail className="w-3 h-3" /> EMAIL
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="upgrade-email"
              placeholder="you@somewhere.net"
              className="input-brutal mt-1 w-full"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/60 flex items-center gap-1">
              <Lock className="w-3 h-3" /> PASSWORD
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="upgrade-password"
              placeholder="min 6 chars"
              className="input-brutal mt-1 w-full"
              required
              minLength={6}
            />
          </div>
          <button
            type="submit"
            disabled={busy || !email.trim() || password.length < 6}
            data-testid="upgrade-submit"
            className="btn-acid w-full mt-2 disabled:opacity-40"
          >
            {busy ? "UPGRADING…" : "LOCK IT IN"}
          </button>
          <p className="font-mono text-[10px] text-white/40 text-center">
            // your @handle and every message stay exactly where they are
          </p>
        </form>
      </div>
    </div>
  );
}
